import {
    FC,
    ReactElement,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useLocation, useNavigate } from 'react-router'

import { NewsfeedContainer } from '@/containers'
import { useFeedConnection } from '@/context/FeedConnectionContext'
import { isRecord } from '@/helpers/isRecord'

const NEWSFEED_POST_QUERY_PARAM = 'post'

interface NewsfeedLocationState {
    focusActivityId?: string
}

const isNewsfeedLocationState = (state: unknown): state is NewsfeedLocationState => (
    isRecord(state) && (state.focusActivityId === undefined || typeof state.focusActivityId === 'string')
)

export const ConnectedNewsfeed: FC = (): ReactElement => {
    const {
        client,
        feed,
        activities,
        loading,
        hasNextPage,
        isLoadingNextPage,
        loadNextPage,
        notifications,
        markAllRead,
        hasConnectionError,
    } = useFeedConnection()

    const navigate = useNavigate()
    const location = useLocation()
    const locationState: unknown = location.state
    const focusActivityIdFromState = isNewsfeedLocationState(locationState) ? locationState.focusActivityId : undefined

    // Deep link from outside the app
    const [queryFocusActivityId] = useState<string | undefined>(
        () => new URLSearchParams(location.search).get(NEWSFEED_POST_QUERY_PARAM) || undefined,
    )

    // Strip the param so a refresh doesn't focus and highlight the post again.
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search)

        if (! searchParams.has(NEWSFEED_POST_QUERY_PARAM)) {
            return
        }

        searchParams.delete(NEWSFEED_POST_QUERY_PARAM)
        navigate(
            { pathname: location.pathname, search: searchParams.toString(), hash: location.hash },
            { replace: true, state: location.state },
        )
    }, [navigate, location])

    const unreadActivityIds = useMemo(
        () => notifications.filter(notification => ! notification.read).map(notification => notification.id),
        [notifications],
    )

    return (
        <NewsfeedContainer
            client={client}
            feed={feed}
            activities={activities}
            loading={loading}
            hasConnectionError={hasConnectionError}
            hasNextPage={hasNextPage}
            isLoadingNextPage={isLoadingNextPage}
            onLoadNextPage={loadNextPage}
            unreadActivityIds={unreadActivityIds}
            onMarkAllRead={markAllRead}
            focusActivityId={focusActivityIdFromState || queryFocusActivityId}
        />
    )
}
